import React from 'react';
import RoomCard from './RoomCard';
import { Building, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function FloorSection({
  floor,
  rooms,
  beds,
  tenants,
  onBedClick,
  onDragStart,
  onDrop,
  onDragOver,
  dragTargetBedId,
  onEditRoom,
  onDeleteRoom,
  onAddRoom,
  onDeleteFloor
}) {
  const floorRooms = rooms.filter(r => r.floorId === floor.id);
  const floorBeds = beds.filter(b => {
    const room = rooms.find(r => r.id === b.roomId);
    return room && room.floorId === floor.id;
  });
  const occupiedBeds = floorBeds.filter(b => b.status === 'occupied').length;
  const totalBeds = floorBeds.length;
  const occupancyPercent = totalBeds > 0 ? (occupiedBeds / totalBeds) * 100 : 0;

  const getHeatmapColor = () => {
    if (occupancyPercent >= 75) return 'from-red-500 to-red-600';
    if (occupancyPercent >= 50) return 'from-yellow-500 to-yellow-600';
    if (occupancyPercent >= 25) return 'from-green-500 to-green-600';
    return 'from-slate-400 to-slate-500';
  };

  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden" data-testid={`floor-${floor.id}`}>
      <div className={`bg-gradient-to-r ${getHeatmapColor()} p-4`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <Building className="w-6 h-6" />
            <div>
              <h3 className="text-lg font-bold">{floor.floorName}</h3>
              <p className="text-sm opacity-90">{floorRooms.length} rooms • {totalBeds} beds</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold">{Math.round(occupancyPercent)}%</div>
              <div className="text-sm opacity-90">{occupiedBeds}/{totalBeds} occupied</div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="bg-white/15 text-white border border-white/30 hover:bg-white/25"
              onClick={() => onAddRoom(floor)}
              data-testid={`add-room-floor-${floor.id}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="bg-red-500/20 text-white border border-white/30 hover:bg-red-500/30"
              onClick={() => onDeleteFloor(floor)}
              data-testid={`delete-floor-${floor.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Floor
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {floorRooms.length === 0 ? (
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50">
            <p className="text-slate-700 font-semibold mb-2">No rooms on this floor yet</p>
            <p className="text-sm text-slate-500 mb-4">Add your first room here to start assigning beds.</p>
            <Button type="button" onClick={() => onAddRoom(floor)} data-testid={`empty-add-room-floor-${floor.id}`}>
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {floorRooms.map((room) => (
              <RoomCard
                key={room.id}
                room={room}
                floor={floor}
                beds={beds}
                tenants={tenants}
                onBedClick={onBedClick}
                onDragStart={onDragStart}
                onDrop={onDrop}
                onDragOver={onDragOver}
                dragTargetBedId={dragTargetBedId}
                onEditRoom={onEditRoom}
                onDeleteRoom={onDeleteRoom}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
